import { useCallback, useEffect, useState } from "react";
import { useLoaderData, useLocation, useSearchParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react"
import Skeleton from "react-loading-skeleton";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { classNames, toGQLPeriod, useIsMounted } from "@ct-react/core";
import { castBookingConfig } from "@ct-react/calendar";
import { LocaleLink } from "@ct-react/locale";
import { ArticleType, ArticleUrlData, buildArticleUrl, buildSearchUrl } from "@shared/url";
import { isRentalArticle, isSaleArticle, RentalAccommodationArticleFilterInput } from "@shared/graphql";
import { actions, common } from "../i18n/sharable-defs";
import { useSearchContext } from "../contexts/search";
import { useBookmarksContext } from "../contexts/bookmarks";
import { parse } from "../tools/search-params/open-article";
import { useTools } from "../hooks/tools";
import { formatSort, formatWhere } from "../hooks/format-gql-variables";
import { useMediaQueryDown } from "../hooks/media-queries";
import { usePeriodParams } from "../hooks/query-params";
import NotFoundPage from "./not-found";
import { Wrapper } from "../components/layout/wrapper";
import { Overlay } from "../components/layout/overlays";
import SeoMetas from "../components/layout/seo-metas";
import SpriteIcon from "../components/medias/sprite-icon";
import Button from "../components/common/button";
import Sharing from "../components/article/sharing";
import MediasPart from "../components/article/medias";
import TitlePart from "../components/article/title";
import LocationPart from "../components/article/location";
import DescriptionPart from "../components/article/description";
import RentalAvailabilitiesPart from "../components/article/rental-availabilities";
import RoomLayoutPart from "../components/article/room-layout";
import FeaturesPart from "../components/article/features";
import ReviewsPart from "../components/article/reviews";
import ContactFormAside from "../components/article/aside/contact-form";
import BookingBoxAside from "../components/article/aside/booking-box";
import Elsje from "../components/common/elsje";
import SaleBoxPart from "../components/article/sale-box";
import ArticleCard from "../components/article-card";
import "./open-article.scss";

const transDefs = defineMessages({
  share: { id: "article.share-title", defaultMessage: "Partager ce bien" },
  description: { id: "article.rubrics.description", defaultMessage: "Description" },
  included: { id: "article.rubrics.included", defaultMessage: "Ce qui est inclus dans la location" },
  additionals: { id: "article.rubrics.additionals", defaultMessage: "Autres informations" },
  location: { id: "article.rubrics.location", defaultMessage: "Emplacement" },
  relatedSubTitle: { id: "article.rubrics.related-subtitle", defaultMessage: "Nos autres biens" },
  back: { id: "article.nav.back", defaultMessage: "Revenir à la recherche " },
  previous: { id: "article.nav.previous", defaultMessage: "Bien précédent" },
  next: { id: "article.nav.next", defaultMessage: "Bien suivant" }
});

const SEO_ARTICLE_GQL_DATA = gql`
  query ArticleSEO($articleId: ID!) {
    forSeo: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle {
        id slug
        seo { title { value } description { value } image keywords }
      }
      ... on SaleAccommodationArticle {
        id slug
        seo { title { value } description { value } image keywords }
      }
    }
  }
`;

const FULL_ARTICLE_GQL_DATA = gql`
  query GetArticle($articleId: ID!) {
    detail: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle {
        id
        title { value }
        description { value matchLang }
        classification
        images { aspectRatio assets { url breakpoint } }
        videos { url }
        virtualVisits { url }
        address { resume city }
        coordinates
        lowestPrice { type onDemand value }
        roomLayout {
          name { value }
          contents { value }
        }
        features {
          maxGuests bedrooms bathrooms livingSpace plotSpace
          withParking withLift withBalcony withTerrace withFireplace withPool withSauna withWIFI withTV withLaundryRoom withWashingMachine withDishwasher nearSkiArea nearBusStop withClearView
          withHotTub withHammam withOven withMicrowave withRacletteSet withFondueSet withHairDryer isPetCompliant isSmokerCompliant isDisabledPersonCompliant withSmokeAlarm withCarbonAlarm withSportFacilities
        }
        costDescriptions { included { value matchLang } }
        additionals { value matchLang }
        touristTax {
          optionId
          grid { ageCategory ageRange price }
        }
      }
      ... on SaleAccommodationArticle {
        id
        title { value }
        description { value matchLang }
        images { aspectRatio assets { url breakpoint } }
        videos { url }
        virtualVisits { url }
        address { resume city }
        coordinates
        price { onDemand value }
        roomLayout {
          name { value }
          contents { value }
        }
        referenceId
        objectType { id }
        features {
          bedrooms bathrooms livingSpace plotSpace rooms
          floor { value } heatings constructionYear isNew isFurnished parkings canBeSecondaryHome isSellableToForeigners
          withParking withLift withBalcony withTerrace withFireplace withPool withSauna withWIFI withTV withLaundryRoom withWashingMachine withDishwasher nearSkiArea nearBusStop withClearView
        }
        additionals { value matchLang }
      }
    }
  }
`;

const SEARCHED_ARTICLE_NAVIGABLE_CTX_GQL_DATA = gql`
  fragment ArticleUrlFields on AnyArticle {
    ... on RentalAccommodationArticle { id slug }
    ... on SeasonalAccommodationArticle { id slug }
    ... on AnnualAccommodationArticle { id slug }
    ... on SaleAccommodationArticle { id slug }
  }
  query SearchedArticleCtx($articleId: ID!, $where: AnyArticleFilterInput, $sort: AnyArticleSortInput) {
    nav: searchedArticleNavContext(articleId: $articleId, where: $where, sort: $sort) {
      previous { ...ArticleUrlFields }
      next { ...ArticleUrlFields }
    }
  }
`;

const ARTICLE_REVIEWS_GQL_DATA = gql`
  query ArticleReviews($articleId: ID!) {
    reviews: bookingAccommodationReviews(articleId: $articleId, length: 20) {
      averageRate
      rates { welcome reservation overallComfort bedsComfort cleanliness accuracy location satisfaction value }
      ratesCount
      comments { reviewedAt customerName averageRate comment answer { answeredAt comment } }
    }
  }
`;

const RELATED_ARTICLE_GQL_DATA = gql`
  query ArticleRelated($where: AnyArticleFilterInput!) {
    search(first: 8, where: $where) {
      totalCount
      edges {
        node {
          ... on RentalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            lowestPrice { type onDemand value }
            features { maxGuests bedrooms bathrooms }
          }
          ... on SaleAccommodationArticle {
            id slug
            novelty recommended promoted sold reserved
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            price { onDemand value }
            features { rooms bedrooms bathrooms livingSpace plotSpace }
          }
        }
      }
    }
  }
`;

const ALL_OPTIONS_GQL_DATA = gql`
  query BookingOptions($articleId: ID!) {
    config: bookingAccommodationConfig(articleId: $articleId) {
      period starters { startingAt dayOffset enders { endingAt bookable } } blocks
    }
  }
`;

const OpenArticlePage = () => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const { format } = useTools();
  const bookmarks = useBookmarksContext();

  const { article: { type: articleType } } = useLoaderData<{ article: { type: ArticleType }}>();

  const location = useLocation();
  const [ searchParams ] = useSearchParams();
  const [ searchContext ] = useSearchContext();
  const [ periodPreset ] = usePeriodParams();

  const [ failure, setFailure ] = useState<boolean>(!parse(searchParams).id);
  const [ articleId, setArticleId ] = useState<string | undefined>(parse(searchParams).id);
  const [ withContextNav, setWithContextNav ] = useState<boolean>(false);

  const { loading: seoLoading, data: seoData } = useQuery<any>(SEO_ARTICLE_GQL_DATA, {
    variables: { articleId }
  });

  const { loading, data, error } = useQuery<any>(FULL_ARTICLE_GQL_DATA, {
    skip: !articleId,
    variables: { articleId },
    ssr: false
  });

  const { data: navData } = useQuery<any>(SEARCHED_ARTICLE_NAVIGABLE_CTX_GQL_DATA, {
    skip: !location.state?.innerNav,
    ssr: false,
    variables: {
      articleId,
      where: formatWhere({
        ...(!!searchContext[articleType].forced && { ...searchContext[articleType].forced }),
        ...searchContext[articleType].saving
      }),
      sort: formatSort(articleType, [ searchContext[articleType].sort ])
    }
  });

  const { loading: reviewsLoading, data: reviewsData } = useQuery<any>(ARTICLE_REVIEWS_GQL_DATA, {
    skip: articleType !== ArticleType.RENTAL,
    variables: { articleId },
    ssr: false
  });

  const { loading: relatedLoading, data: relatedData } = useQuery<any>(RELATED_ARTICLE_GQL_DATA, {
    skip: !articleId,
    variables: { where: formatWhere({ ...searchContext[articleType].saving, excludedIds: [ articleId! ] }) },
    ssr: false
  });

  const { stopPolling, loading: availabilitiesLoading, data: availabilitiesData } = articleType !== ArticleType.RENTAL
    ? { stopPolling: () => void 0, loading: false, data: undefined }
    : useQuery<any>(ALL_OPTIONS_GQL_DATA, {
      skip: !articleId,
      variables: { articleId },
      ssr: false,
      fetchPolicy: "no-cache",
      pollInterval: 30 * 1000
    });

  useEffect(() => {
    return () => { articleType === ArticleType.RENTAL && stopPolling(); }
  }, []);

  useEffect(() => {
    !!error && setFailure(true);
  }, [ error ]);

  useEffect(() => {
    if (!isMounted) return;
    const id = parse(searchParams).id;
    if (articleId === id) return;
    !!id ? setArticleId(id) : setFailure(true);
  }, [ searchParams ]);

  useEffect(() => {
    withContextNav !== !!location.state?.innerNav && setWithContextNav(!!location.state?.innerNav);
  }, [ location.state ]);

  const buildNavTarget = useCallback((data: ArticleUrlData) => {
    const url = buildArticleUrl(data);
    const period = (searchContext[articleType].saving as RentalAccommodationArticleFilterInput).period?.period;
    if (data.__typename !== ArticleType.RENTAL || !period) return url;
    const formatted = toGQLPeriod(period);
    return `${url}&b_in=${formatted[0]}&b_out=${formatted[1]}`;
  }, [ searchContext[articleType].saving ]);

  if (failure)
    return (<NotFoundPage />);

  const withStickyAside = useMediaQueryDown("mobile");
  const asideClasses = classNames("right", { "not-sticked": !withStickyAside });

  return (
    <article key={articleId} className="open-article">

      {(!seoLoading && !!seoData?.forSeo) &&
        <SeoMetas {...seoData.forSeo.seo}
                  canonical={buildArticleUrl(seoData.forSeo)} />
      }

      <Wrapper.Centered className="article-head content">

        <div className="top-nav">
          {withContextNav &&
            <LocaleLink to={buildSearchUrl(articleType)} className="nav-link">
              <IconChevronLeft className="n-icon" />
              {intl.formatMessage(transDefs.back)}
            </LocaleLink>
          }
          {loading
            ? <Skeleton containerClassName="actions" inline={true} count={3}
                        className="sprite-icon" width={37.2} height={37.2} borderRadius="50%" />
            : (
              <div className="actions">

                <Button type="button" className="small with-icon outline"
                        onClick={() => bookmarks.toggle(articleType, articleId!)}>
                  <SpriteIcon hash={bookmarks.check(articleType, articleId!) ? "action.bookmarked" : "action.bookmark"} />
                </Button>

                <Overlay.Modal size="sm">
                  <Overlay.ModalTrigger>
                    <Button type="button" className="small with-icon outline">
                      <SpriteIcon hash="action.share" />
                    </Button>
                  </Overlay.ModalTrigger>
                  <Overlay.ModalContent title={intl.formatMessage(transDefs.share)}>
                    <Sharing loading={loading}
                             {...(!!data?.detail) && {
                               data: {
                                 title: data.detail.title,
                                 address: data.detail.address.resume,
                                 image: data.detail.images[0]
                               }
                             }} />
                  </Overlay.ModalContent>
                </Overlay.Modal>

                <Button type="button" className="small with-icon outline"
                        onClick={() => window.print()}>
                  <SpriteIcon hash="action.print" />
                </Button>

              </div>)
          }
        </div>

        <MediasPart loading={loading}
                    {...(!!data?.detail) && {
                      data: {
                        images: data.detail.images,
                        videos: data.detail.videos,
                        virtualVisits: data.detail.virtualVisits
                      }
                    }} />

        {withContextNav &&
          <div className="bottom-nav">
            <div className="previous">
              {!!navData?.nav.previous &&
                <LocaleLink className="nav-link"
                            to={buildNavTarget(navData.nav.previous)}
                            state={{ innerNav: true }}>
                  <IconChevronLeft className="n-icon" />
                  {intl.formatMessage(transDefs.previous)}
                </LocaleLink>
              }
            </div>
            <div className="next">
              {!!navData?.nav.next &&
                <LocaleLink className="nav-link"
                            to={buildNavTarget(navData.nav.next)}
                            state={{ innerNav: true }}>
                  {intl.formatMessage(transDefs.next)}
                  <IconChevronRight className="n-icon" />
                </LocaleLink>
              }
            </div>
          </div>
        }

      </Wrapper.Centered>

      <Wrapper.Centered className="two-col content">

        <div className="left">

          <TitlePart loading={loading}
                     className="separator"
                     {...(!!data?.detail) && {
                       data: {
                         title: data.detail.title,
                         subTitle: data.detail.address.city,
                         features: data.detail.features,
                         ...(isSaleArticle(data.detail) && { price: data.detail.price })
                       }
                     }}/>

          <DescriptionPart displayLoading={true}
                           loading={loading}
                           className="separator"
                           {...(!!data?.detail) && { data: data.detail.description }} />

          {(!!data?.detail && isRentalArticle(data.detail)) &&
            <RentalAvailabilitiesPart className="separator"
                                      loading={availabilitiesLoading}
                                      {...(!!availabilitiesData) && {
                                        data: castBookingConfig(availabilitiesData.config)
                                      }} />
          }

          <RoomLayoutPart loading={loading}
                          className="separator"
                          {...data?.detail.roomLayout?.length > 0 && {
                            data: data.detail.roomLayout
                          }} />

          <FeaturesPart loading={loading}
                        className="separator"
                        type={articleType}
                        {...(!!data?.detail) && {
                          data: {
                            ...(articleType === ArticleType.SALE) && {
                              referenceId: data.detail.referenceId,
                              ...(!!data.detail.objectType) && { objectType: data.detail.objectType.id },
                            },
                            ...data.detail.features
                          }
                        }}/>

          {(articleType === ArticleType.RENTAL && !!data?.detail.costDescriptions?.included ) &&
            <div className="separator">
              <h2>{intl.formatMessage(transDefs.included)}</h2>
              <DescriptionPart loading={loading}
                               {...(!!data?.detail) && { data: data.detail.costDescriptions.included }} />
            </div>
          }

          {!!data?.detail.additionals &&
            <div className="separator">
              <h2>{intl.formatMessage(transDefs.additionals)}</h2>
              <DescriptionPart loading={loading}
                               {...(!!data?.detail) && { data: data.detail.additionals }} />
            </div>
          }

        </div>

        <aside className={asideClasses}>
          {(!loading && !!articleId && !!data?.detail) &&
            <div className="aside-wrapper">
              {(() => {
                if (isRentalArticle(data.detail))
                  return (
                    <>
                      {(() => {
                        const shared = <BookingBoxAside activator={withStickyAside}
                                                        data={{
                                                          articleId,
                                                          lowestPrice: data.detail.lowestPrice,
                                                          ...(!!availabilitiesData) && {
                                                            config: castBookingConfig(availabilitiesData.config)
                                                          },
                                                          touristTaxConfig: data.detail.touristTax,
                                                          maxGuests: data.detail.features.maxGuests
                                                        }} />
                        return withStickyAside ? shared : (
                          <div className="boxed book">
                            <h2 className="ellipsed">
                              {!!periodPreset
                                ? intl.formatMessage(actions.bookHoliday)
                                : format.anyPrice(data.detail.lowestPrice)}
                            </h2>
                            <div className="inner">{shared}</div>
                          </div>);
                      })()}
                      {!withStickyAside && <Elsje />}
                    </>);
                return (
                  <>
                    {!withStickyAside &&
                      <>
                        <h2>{`${intl.formatMessage(actions.contactUs)} !`}</h2>
                        <Elsje />
                      </>
                    }
                    <ContactFormAside activator={withStickyAside}
                                      resume={{
                                        title: data.detail.title,
                                        address: data.detail.address.resume,
                                        image: data.detail.images[0]
                                      }}
                                      data={{ articleId, price: data.detail.price }} />
                    {!withStickyAside && <SaleBoxPart />}
                  </>);
              })()}
            </div>
          }
        </aside>

      </Wrapper.Centered>

      <Wrapper.Centered className="content">
        <LocationPart loading={loading}
                      title={intl.formatMessage(transDefs.location)}
                      {...(!!data?.detail && {
                        data: {
                          address: data.detail.address,
                          coordinates: data.detail.coordinates
                        }
                      })} />
      </Wrapper.Centered>

      {(articleType === ArticleType.RENTAL && !reviewsLoading && !!reviewsData?.reviews) &&
        <ReviewsPart className="separator"
                     data={reviewsData?.reviews} />
      }

      {(articleType === ArticleType.SALE && withStickyAside) &&
        <SaleBoxPart className="separator" asWrapper={true} />
      }

      {(relatedLoading || (!!relatedData && relatedData?.search.totalCount > 1)) &&
        <Wrapper.ScrollableList className="content related">
          <Wrapper.ScrollableListHeader title={intl.formatMessage(common.relatedTitle)}
                                        subTitle={intl.formatMessage(transDefs.relatedSubTitle)} />
          <Wrapper.ScrollableListContent>
            {(relatedData?.search.edges || Array.from({ length: 3 })).map((edge: any, index: number) =>
              <ArticleCard key={edge?.node.id || index}
                           loading={relatedLoading}
                           data={edge?.node} />
            )}
          </Wrapper.ScrollableListContent>
        </Wrapper.ScrollableList>
      }

    </article>);

};

export default OpenArticlePage;
